import type { ReactNode } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, Field, Select, Textarea } from '@paige/ui';
import '../admin.css';

/** What the API records before a staff session may open a company (architecture §14.4). */
export interface SupportAccessRequest {
  reason: string;
  minutes: number;
  /** read (the default) only looks: the API refuses every change; write is for billing and engineering. */
  mode?: 'read' | 'write';
}

export interface SupportAccessDialogProps {
  companyName: string;
  busy?: boolean;
  onClose: () => void;
  onSubmit: (request: SupportAccessRequest) => void;
  /** Billing and engineering may ask to act for the company; support only looks. */
  mayAct?: boolean;
  /** The reason to start from, when the screen that opened it knows why (an incident on Stages and queues). */
  defaultReason?: string;
}

/** The time boxes offered; the API accepts 1 to 480 minutes. */
export const supportAccessMinutes = [15, 30, 60, 120, 240] as const;

export const supportAccessSchema = z.object({
  reason: z.string().trim().min(8, 'Say why, in at least a few words'),
  minutes: z.coerce.number().int().min(1).max(480),
  act: z.boolean().default(false),
});

type FormInput = z.input<typeof supportAccessSchema>;
type FormValues = z.output<typeof supportAccessSchema>;

/**
 * "Open as support": asks for the reason and the time box, both recorded with the staff member's
 * name; the company opens only once the grant exists.
 */
export function SupportAccessDialog({
  companyName,
  busy,
  onClose,
  onSubmit,
  mayAct,
  defaultReason,
}: SupportAccessDialogProps): ReactNode {
  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(supportAccessSchema),
    defaultValues: { reason: defaultReason ?? '', minutes: 60, act: false },
  });
  const submit = form.handleSubmit((v) =>
    onSubmit({ reason: v.reason, minutes: v.minutes, mode: mayAct && v.act ? 'write' : 'read' }),
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Open ${companyName} as support`}
      description="A recorded, time-boxed grant, read-only unless you ask to act. The company's admins are told, with your reason."
      size="sm"
      actions={<AccessButtons onClose={onClose} busy={busy ?? false} />}
    >
      <form id="ad-access-form" className="pui-form" onSubmit={(e) => void submit(e)} noValidate>
        <AccessFields form={form} />
        <ModeLine mayAct={mayAct ?? false} form={form} />
      </form>
    </Dialog>
  );
}

function AccessFields({
  form,
}: {
  form: UseFormReturn<FormInput, unknown, FormValues>;
}): ReactNode {
  const err = form.formState.errors;
  return (
    <>
      <Field
        label="Reason"
        required
        error={err.reason?.message}
        hint="Ticket or request, in your words"
      >
        {({ id, describedBy }) => (
          <Textarea id={id} aria-describedby={describedBy} {...form.register('reason')} />
        )}
      </Field>
      <Field label="For how long" required error={err.minutes?.message}>
        {({ id }) => (
          <Select id={id} {...form.register('minutes')}>
            {supportAccessMinutes.map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} minutes` : `${m / 60} hour${m > 60 ? 's' : ''}`}
              </option>
            ))}
          </Select>
        )}
      </Field>
    </>
  );
}

/** Billing and engineering may ask to act; everyone else is told the view is read-only. */
function ModeLine({
  mayAct,
  form,
}: {
  mayAct: boolean;
  form: UseFormReturn<FormInput, unknown, FormValues>;
}): ReactNode {
  return mayAct ? (
    <label className="ad-check">
      <input type="checkbox" {...form.register('act')} /> Act for the company: changes allowed and
      audited
    </label>
  ) : (
    <p className="ad-small">Read-only: Paige refuses every change while you look.</p>
  );
}

function AccessButtons({ onClose, busy }: { onClose: () => void; busy: boolean }): ReactNode {
  return (
    <>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" type="submit" form="ad-access-form" loading={busy}>
        Open as support
      </Button>
    </>
  );
}
